import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, MenuItem, LinearProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { EditCalendar, Add, CheckCircle, Cancel, Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  pending:   { color: 'warning', label: '⏳ Pending' },
  approved:  { color: 'success', label: '✅ Approved' },
  rejected:  { color: 'error',   label: '❌ Rejected' },
  cancelled: { color: 'default', label: '🚫 Cancelled' },
};

const TYPE_LABELS: Record<string, string> = {
  clock_in:  'Fix Clock In',
  clock_out: 'Fix Clock Out',
  both:      'Fix Both',
  full_day:  'Full Day',
  status:    'Fix Status',
};

interface Correction { id: string; employeeId: string; date: string; correctionType: string; status: string; requestedClockIn?: string; requestedClockOut?: string; requestedStatus?: string; reason: string; reviewerNote?: string; reviewedAt?: string; employee?: { firstName: string; lastName: string; department?: string }; }

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  correctionType: 'both',
  requestedClockIn: '09:00',
  requestedClockOut: '18:00',
  reason: '',
});

const STATUSES = ['work_from_home', 'present', 'absent', 'late', 'half_day', 'on_leave'];

export default function AttendanceCorrection() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'manager'].includes(user?.role ?? '');

  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [pending, setPending] = useState<Correction[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [tab, setTab] = useState<'mine' | 'pending'>('mine');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<any>(emptyForm());
  const [reviewDialog, setReviewDialog] = useState<{ id: string; action: 'approve' | 'reject'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [mine, pend, st] = await Promise.all([
        apiFetch('/api/attendance-corrections'),
        isHR ? apiFetch('/api/attendance-corrections/pending') : Promise.resolve([]),
        apiFetch('/api/attendance-corrections/stats'),
      ]);
      setCorrections(mine || []); setPending(pend || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await apiFetch('/api/attendance-corrections', { method: 'POST', body: JSON.stringify(form) });
      setFormOpen(false); setForm(emptyForm()); load();
    } catch (e: any) { setError(e.message); }
  };

  const review = async () => {
    if (!reviewDialog) return;
    try {
      await apiFetch(`/api/attendance-corrections/${reviewDialog.id}/${reviewDialog.action}`, {
        method: 'PATCH', body: JSON.stringify({ reviewerNote: reviewDialog.note }),
      });
      setReviewDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    try {
      await apiFetch(`/api/attendance-corrections/${id}/cancel`, { method: 'PATCH' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const list = tab === 'mine' ? corrections : pending;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditCalendar sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Attendance Corrections</Typography>
            <Typography variant="body2" color="text.secondary">Request fixes for missed or incorrect clock-in/out records</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setForm(emptyForm()); setFormOpen(true); }}>
          New Request
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total',    value: stats.total,    color: '#37474f' },
            { label: 'Pending',  value: stats.pending,  color: '#e65100' },
            { label: 'Approved', value: stats.approved, color: '#2e7d32' },
            { label: 'Rejected', value: stats.rejected, color: '#c62828' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tab toggle */}
      {isHR && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip label={`My Requests (${corrections.length})`} onClick={() => setTab('mine')}
            color={tab === 'mine' ? 'primary' : 'default'} variant={tab === 'mine' ? 'filled' : 'outlined'} />
          <Chip label={`Pending Review (${pending.length})`} onClick={() => setTab('pending')}
            color={tab === 'pending' ? 'warning' : 'default'} variant={tab === 'pending' ? 'filled' : 'outlined'} />
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              {isHR && tab === 'pending' && <TableCell><b>Employee</b></TableCell>}
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>Requested</b></TableCell>
              <TableCell><b>Reason</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              {tab === 'pending' && <TableCell><b>Actions</b></TableCell>}
              {tab === 'mine' && <TableCell><b>Actions</b></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(c => (
              <TableRow key={c.id} hover>
                {isHR && tab === 'pending' && (
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{c.employee?.firstName} {c.employee?.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.employee?.department}</Typography>
                  </TableCell>
                )}
                <TableCell>{new Date(c.date + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                <TableCell><Chip label={TYPE_LABELS[c.correctionType] ?? c.correctionType} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {c.correctionType === 'status' ? `Status → ${c.requestedStatus}` :
                      [c.requestedClockIn && `In: ${c.requestedClockIn}`, c.requestedClockOut && `Out: ${c.requestedClockOut}`].filter(Boolean).join(' / ')}
                  </Typography>
                  {c.reviewerNote && <Typography variant="caption" color="text.secondary" display="block">Note: {c.reviewerNote}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ maxWidth: 200, display: 'block' }}>{c.reason}</Typography>
                </TableCell>
                <TableCell><Chip label={STATUS_CFG[c.status]?.label} size="small" color={STATUS_CFG[c.status]?.color} /></TableCell>
                {tab === 'pending' && isHR && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => setReviewDialog({ id: c.id, action: 'approve', note: '' })}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => setReviewDialog({ id: c.id, action: 'reject', note: '' })}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
                {tab === 'mine' && (
                  <TableCell>
                    {c.status === 'pending' && (
                      <Tooltip title="Cancel Request">
                        <IconButton size="small" onClick={() => cancel(c.id)}><Close fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {list.length === 0 && !loading && (
              <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {tab === 'pending' ? 'No pending correction requests.' : 'No correction requests yet.'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── FORM DIALOG ── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          New Attendance Correction
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={form.date} onChange={e => setForm((p: any) => ({ ...p, date: e.target.value }))} />
            <TextField label="Correction Type" select fullWidth value={form.correctionType}
              onChange={e => setForm((p: any) => ({ ...p, correctionType: e.target.value }))}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            {['clock_in', 'both'].includes(form.correctionType) && (
              <TextField label="Correct Clock-In Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={form.requestedClockIn ?? '09:00'} onChange={e => setForm((p: any) => ({ ...p, requestedClockIn: e.target.value }))} />
            )}
            {['clock_out', 'both'].includes(form.correctionType) && (
              <TextField label="Correct Clock-Out Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={form.requestedClockOut ?? '18:00'} onChange={e => setForm((p: any) => ({ ...p, requestedClockOut: e.target.value }))} />
            )}
            {form.correctionType === 'status' && (
              <TextField label="Correct Status" select fullWidth value={form.requestedStatus ?? 'present'}
                onChange={e => setForm((p: any) => ({ ...p, requestedStatus: e.target.value }))}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Reason *" fullWidth multiline rows={3} required
              value={form.reason} onChange={e => setForm((p: any) => ({ ...p, reason: e.target.value }))}
              placeholder="Explain why a correction is needed (e.g. forgot to clock in due to meeting)" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={!form.reason.trim() || !form.date}>Submit Request</Button>
        </DialogActions>
      </Dialog>

      {/* ── REVIEW DIALOG ── */}
      <Dialog open={!!reviewDialog} onClose={() => setReviewDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{reviewDialog?.action === 'approve' ? '✅ Approve Correction' : '❌ Reject Correction'}</DialogTitle>
        <DialogContent dividers>
          <TextField label={reviewDialog?.action === 'reject' ? 'Rejection reason *' : 'Note (optional)'}
            fullWidth multiline rows={2}
            value={reviewDialog?.note ?? ''}
            onChange={e => setReviewDialog(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(null)}>Cancel</Button>
          <Button variant="contained"
            color={reviewDialog?.action === 'approve' ? 'success' : 'error'}
            disabled={reviewDialog?.action === 'reject' && !reviewDialog?.note?.trim()}
            onClick={review}>
            {reviewDialog?.action === 'approve' ? 'Approve & Apply' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
